import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router' 
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ArrowLeft, XCircle } from "lucide-react";

interface JobDetail {
  id: string;
  type: string;
  status: string;
  input: unknown;
  output: unknown;
  error: string | null;
  creditsUsed: number;
  executionTime: number | null;
  nodeId: string | null;
  retryCount: number;
  maxRetries: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  user: { id: string; email: string; name: string };
  canvas: { uuid: string; title: string };
}

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "text-green-600", FAILED: "text-red-600", RUNNING: "text-yellow-600", PENDING: "text-gray-500", CANCELED: "text-gray-400",
};

function fmtDate(d: string | null) {
  return d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-";
}

export const Route = createFileRoute('/dashboard/jobs/$id')({
  component: JobDetailPage,
})

function JobDetailPage() {
  const navigate = useNavigate();
  const { id: jobId } = Route.useParams();
  const [job, setJob] = React.useState<JobDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState(false);

  React.useEffect(() => { fetchJob(); }, [jobId]);

  async function fetchJob() {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/jobs/${jobId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      const json = await res.json();
      setJob(json.data.job);
      document.title = `Job ${jobId.slice(0, 8)} - Admin`;
    } catch { console.error("Failed to load job"); } finally { setLoading(false); }
  }

  async function handleForceCancel() {
    if (!confirm("Force cancel this job? Credits will be refunded if deducted.")) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/jobs/${jobId}/force-cancel`, { method: "POST", credentials: "include" });
      const json = await res.json();
      if (json.success) { alert(`Canceled. Refunded: ${json.data.refunded} credits`); fetchJob(); }
      else alert(json.error);
    } finally { setActionLoading(false); }
  }

  if (loading) return <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">Loading...</p></div>;
  if (!job) return <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">Job not found</p></div>;

  // No Retry action: api-admin has no queue client, so POST /jobs/:id/retry answers 501.
  // A failed job is re-run from its node on the canvas.
  const canCancel = job.status === "RUNNING" || job.status === "PENDING";

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="/dashboard/jobs">Jobs</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>{job.id.slice(0, 8)}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate({ to: "/dashboard/jobs" })}><ArrowLeft className="h-4 w-4" /></Button>
                <h1 className="text-xl font-bold font-mono">{job.id}</h1>
                <Badge variant="outline" className={STATUS_COLORS[job.status]}>{job.status}</Badge>
                <Badge variant="outline">{job.type.replace(/_/g, " ")}</Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                User: <span className="text-foreground cursor-pointer hover:underline" onClick={() => navigate({ to: `/dashboard/users/${job.user.id}` })}>{job.user.email}</span>
                {" · "}Canvas: <span className="text-foreground cursor-pointer hover:underline" onClick={() => navigate({ to: `/dashboard/canvas/${job.canvas.uuid}` })}>{job.canvas.title}</span>
                {" · "}{job.creditsUsed} credits
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {canCancel && <Button variant="destructive" size="sm" onClick={handleForceCancel} disabled={actionLoading}><XCircle className="h-3.5 w-3.5 mr-1" />Force Cancel</Button>}
            </div>
          </div>
        </div>
      </header>
      <main className="px-4 py-6 md:px-6 space-y-6">
        {/* Timeline */}
        <div className="flex gap-8 flex-wrap">
          <div><span className="text-muted-foreground text-xs block">Created</span><span className="text-sm font-medium">{fmtDate(job.createdAt)}</span></div>
          <div><span className="text-muted-foreground text-xs block">Started</span><span className="text-sm font-medium">{fmtDate(job.startedAt)}</span></div>
          <div><span className="text-muted-foreground text-xs block">Completed</span><span className="text-sm font-medium">{fmtDate(job.completedAt)}</span></div>
          <div><span className="text-muted-foreground text-xs block">Duration</span><span className="text-sm font-medium">{job.executionTime ? `${job.executionTime}s` : "-"}</span></div>
          <div><span className="text-muted-foreground text-xs block">Retries</span><span className="text-sm font-medium">{job.retryCount}/{job.maxRetries}</span></div>
        </div>

        <Tabs defaultValue={job.error ? "error" : "input"}>
          <TabsList>
            <TabsTrigger value="input">Input</TabsTrigger>
            <TabsTrigger value="output">Output</TabsTrigger>
            {job.error && <TabsTrigger value="error" className="text-red-500">Error</TabsTrigger>}
          </TabsList>
          <TabsContent value="input" className="mt-4">
            <pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[500px]">{JSON.stringify(job.input, null, 2)}</pre>
          </TabsContent>
          <TabsContent value="output" className="mt-4">
            {job.output ? (
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[500px]">{JSON.stringify(job.output, null, 2)}</pre>
            ) : (
              <p className="text-muted-foreground">No output</p>
            )}
          </TabsContent>
          {job.error && (
            <TabsContent value="error" className="mt-4">
              <pre className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-xs overflow-auto max-h-[500px] text-red-500">{job.error}</pre>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
